<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="complexes">
					<!-- Heading -->
					<app-heading :title="`${website.name} Complexes`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('website.post') && website.connection"
								class="d-none d-sm-inline-flex"
								color="success"
								outlined
								small
								:to="{ name: 'website-complexes-activate' }"
							>Activate Complexes</app-button>
						</template>
						<template v-slot:right>
							<input-file
								type="json"
								button
								small
								outlined
								label="Import SEO"
								@load="onImport"
							/>
						</template>
					</app-heading>

					<app-row v-if="website.connection">
						<app-cell>
							<!-- Bulk Actions -->
							<div class="pb-4">
								<app-button
									v-if="$ravello.can('website.put')"
									color="secondary"
									icon="fa-tags"
									small
									:disabled="!selected || !selected.length"
									@click="onTagsSelected()"
								>
									Tags
								</app-button>
								<app-button
									v-if="$ravello.can('website.put')"
									color="warning"
									icon="fa-ban"
									small
									:disabled="!selected || !selected.length"
									@click="onUpdateSelected({ active: false })"
								>
									Deactivate
								</app-button>
								<app-button
									v-if="$ravello.can('website.put')"
									color="success"
									icon="fa-check"
									small
									:disabled="!selected || !selected.length"
									@click="onUpdateSelected({ active: true })"
								>
									Reactivate
								</app-button>
								<app-button-confirm
									v-if="$ravello.can('website.delete')"
									color="error"
									icon="fa-trash"
									small
									label-confirm="Purge"
									:description="`Are you sure you want permently purge content for `
										+ `selected complexes?`"
									title="Purge Content"
									:disabled="!selected || !selected.length"
									@click="onDeleteSelected()"
								>
									Purge
								</app-button-confirm>
							</div>

							<!-- Data table -->
							<app-data-table
								:items="complexes"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								:select="$ravello.can('website.put')"
								search
								@select="updateSelection"
								@update="fetchComplexes"
								@search="onSearch"
							>
								<template v-slot:active="{ item }">
									<app-icon v-if="item.active" icon="fa-check-circle" color="success" />
									<app-icon v-else icon="fa-times-circle" color="error" />
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('website.put') && item.active"
										color="secondary"
										icon="fa-edit"
										table
										:to="{
											name: 'website-complex',
											params: { website: websiteId, complex: item._id }
										}"
									>
										Configure
									</app-button>
									<app-button
										v-if="$ravello.can('website.put') && item.active"
										color="secondary"
										icon="fa-tags"
										table
										@click="onTags(item)"
									>
										Tags
									</app-button>
									<app-button
										v-if="$ravello.can('website.put') && item.active"
										color="warning"
										icon="fa-ban"
										table
										@click="onUpdate(item._id, { active: false }, true)"
									>
										Deactivate
									</app-button>
									<app-button
										v-if="$ravello.can('website.put') && !item.active"
										color="success"
										icon="fa-check"
										table
										@click="onUpdate(item._id, { active: true }, true)"
									>
										Reactivate
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('website.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Purge"
										:description="`Are you sure you want permanently purge content for `
											+ `<b>${item.complex && item.complex.name}</b>?`"
										title="Purge Content"
										@click="onDelete(item._id)"
									>
										Purge
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
					<app-row v-else>
						<app-alert
							:dismissible="false"
							:message="`No integration connection found for this website. `
							+ `Please add a connection for this website or contact an administrator.`"
						/>
					</app-row>

					<!-- Tags dialog box -->
					<app-dialog
						v-model="tags.open"
						max-width="500"
						title="Manage Tags"
						persistent
					>
						<app-dialog-body>
							<app-row>
								<app-cell>
									<input-combobox
										v-model="tags.value"
										:items="tags.items"
										color="secondary"
										label="Tags"
										multiple
									>
									</input-combobox>
								</app-cell>
							</app-row>
						</app-dialog-body>
						<app-dialog-footer>
							<app-button
								color="secondary"
								flat="flat"
								@click="onTagsUpdated"
							>Save</app-button>
							<app-button
								flat="flat"
								@click="tags.open = false"
							>Cancel</app-button>
						</app-dialog-footer>
					</app-dialog>

					<!-- Floating button -->
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'website-complexes-activate' }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { websiteContentTabs } from '@/datasources/tabs'
import { mapAll } from '@/utils/async'
import { importCollection2 } from '@/utils/import'
import Tags from '@/mixins/tags'

export default {
	mixins: [Tags],
	data() {
		return {
			website: {
				connection: null,
			},
			complexes: [],
			// @todo - Find a better long-term solution
			datasource: [],
			searchTerm: null,
			selected: [],
			count: -1,
			refresh: false,
			tabs: [],
			tags: {
				open: false,
				id: null,
				value: [],
				items: [],
				init: false,
			},
			websiteTags: [],
			headers: [{
				text: 'Active?',
				value: 'active',
				sortable: true,
			}, {
				text: 'Name',
				value: 'complex.name',
				sortable: true,
			}, {
				text: 'URI name',
				value: 'uri_name',
				sortable: true,
			}, {
				text: 'Tags',
				value: 'tags',
				sortable: false,
				cb: item => (item.tags || []).join(', '),
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
		}
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
	},
	async mounted() {
		this.website = await this.$ravello.graphql('website', this.websiteId)
			.fields(['name', 'template'])
			.include(this.$ravello.graphql('connection').field('_id'))
			.query()
		await this.loadMap()
		this.tabs = websiteContentTabs(this.websiteId)
	},
	methods: {
		/* @todo - Find a better long term solution */
		async loadMap() {
			const datasource = []
			let entries = []
			let offset = 0
			do {
				const request = this.$ravello.graphql('complexes')
					.parent(this.$ravello.graphql('website', this.websiteId))
					.include(this.$ravello.graphql('complex').fields(['name']))
					.fields(['_id'])
					.limit(100)
					.offset(offset)

				// eslint-disable-next-line no-await-in-loop
				entries = await request.query()
				datasource.push(...entries.map(entry => ({
					_id: entry._id,
					name: entry.complex && entry.complex.name,
				})))
				offset += 100
			} while (entries.length === 100)
			this.datasource = datasource
		},
		sortFromMap({ page, limit, sort }, includeOnlyIds, prefix) {
			const offset = (page - 1) * limit
			const sortField = sort.replace(prefix ? `${prefix}.` : '', '').replace(/^-/, '')
			const isDesc = sort.charAt(0) === '-'

			const datasource = [...this.datasource]
			return datasource
				.filter(({ _id }) => !includeOnlyIds || includeOnlyIds.includes(_id))
				.sort((a, b) => a[sortField].localeCompare(b[sortField]) * (isDesc ? -1 : 1))
				.map(({ _id }) => _id)
				.slice(offset, offset + limit)
		},
		searchFromMap(term) {
			const datasource = [...this.datasource]
			const regExp = new RegExp(term, 'i')

			return datasource.filter(data => regExp.test(data.name)).map(({ _id }) => _id).slice(0, 10)
		},
		/* END @todo - Find a better long term solution */

		async fetchComplexes(pagination) {
			let sortIds = null
			const searchIds = this.searchTerm ? this.searchFromMap(this.searchTerm) : null

			const request = this.$ravello.graphql('complexes')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.include(this.$ravello.graphql('complex').fields(['name']))
				.fields(['_id', 'active', 'tags', 'uri_name'])

			if (['complex.name', '-complex.name'].includes(pagination.sort)) {
				sortIds = this.sortFromMap(pagination, searchIds, 'complex')
				request.filter('_id', sortIds)
				request.limit(sortIds.length)
			} else if (searchIds) {
				request.filter('_id', searchIds)
				request.limit(searchIds.length)
			} else {
				request.fromPagination(pagination)
			}

			if (this.count === -1 && !searchIds && !sortIds) {
				request.count('count')
			}

			try {
				const complexes = await request.query()
				if (sortIds) {
					this.complexes = complexes.sort((a, b) => sortIds.indexOf(a._id) - sortIds.indexOf(b._id))
				} else {
					this.complexes = complexes
				}
				if (this.count === -1 && !searchIds && !sortIds) {
					this.count = request.results.count
				} else if (searchIds) {
					this.count = searchIds.length
				} else if (sortIds) {
					this.count = this.datasource.length
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onSearch(term) {
			this.searchTerm = term
			this.count = -1
		},

		updateSelection(selected) {
			this.selected = selected
		},

		async onUpdate(id, updates, reload = false) {
			if (reload) {
				this.$store.dispatch('app/startLoading')
			}

			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('complexes')
					.to(id)
					.update(updates)
				if (reload) {
					this.refresh = true
				} else {
					this.$notifications.pushAlert('success', 'Complex preferences updated')
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			if (reload) {
				this.$store.dispatch('app/stopLoading')
			}
		},

		async onUpdateSelected(updates, useSelection = true) {
			this.$store.dispatch('app/startLoading')

			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('complexes')
					.update(useSelection ? this.selected.map(id => ({
						...updates,
						_id: id,
					})) : updates)

				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		async deleteOne(id) {
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('complexes')
					.to(id)
					.delete()
				return true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			return false
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			const res = await this.deleteOne(id)
			if (res) {
				this.refresh = true
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onDeleteSelected() {
			this.$store.dispatch('app/startLoading')
			await mapAll(this.selected, id => this.deleteOne(id))
			this.refresh = true
			this.$store.dispatch('app/stopLoading')
		},

		async initTags() {
			if (this.tags.init) { return }
			this.tags.items = await this.loadWebsiteTags(this.websiteId)
		},

		async onTags(item) {
			await this.initTags()
			this.tags.id = item._id
			this.tags.value = item.tags ? [...item.tags] : []
			this.tags.open = true
		},

		async onTagsSelected() {
			await this.initTags()
			this.tags.id = this.selected
			this.tags.value = this.getTags(this.complexes, this.selected)
			this.tags.open = true
		},

		onTagsUpdated() {
			if (Array.isArray(this.tags.id)) {
				const updates = this.getTagsUpdates(this.complexes, this.tags.id, this.tags.value)
				this.onUpdateSelected(updates, false)
			} else {
				this.onUpdate(this.tags.id, { tags: this.tags.value }, true)
			}
			this.tags.open = false
		},

		async getResortComplexesMapFromImportData(importData) {
			const resorts = importData.map(({ resort }) => resort)
			const ids = importData.map(({ id }) => id)
			const resortCompletes = await this.$ravello.graphql('resorts')
				.filter('studeo_id', resorts)
				.limit(resorts.length)
				.field('studeo_id')
				.include(this.$ravello.graphql('complexes')
					.filter('studeo_id', ids)
					.fields(['studeo_id', '_id'])
					.limit(ids.length))
				.query()
			return resortCompletes.reduce((acc, resortData) => {
				resortData.complexes.forEach((complex) => {
					acc[complex._id] = {
						resort: resortData.studeo_id,
						id: complex.studeo_id,
					}
				})

				return acc
			}, {})
		},
		async getWebsiteComplexesMapFromImportData(importData) {
			const resortComplexesMap = await this.getResortComplexesMapFromImportData(importData)
			const ids = Object.keys(resortComplexesMap)
			const websiteComplexes = await this.$ravello.graphql('complexes')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.filter('complex', ids)
				.include(this.$ravello.graphql('complex').field('_id'))
				.field('_id')
				.limit(ids.length)
				.query()

			return websiteComplexes.reduce((acc, websiteComplex) => {
				const data = resortComplexesMap[websiteComplex.complex._id]
				if (!data) { return acc }
				if (!acc[data.resort]) {
					acc[data.resort] = {}
				}
				acc[data.resort][data.id] = websiteComplex._id

				return acc
			}, {})
		},

		getObjectIdsInImportData(map, importData) {
			return importData.map(({ resort, id, ...rest }) => ({
				...rest,
				_id: map[resort] && map[resort][id],
			}))
		},

		async onImport(importData) {
			this.$store.dispatch('app/startLoading')
			const map = await this.getWebsiteComplexesMapFromImportData(importData)
			const data = this.getObjectIdsInImportData(map, importData)

			const request = this.$ravello.request()
				.v(2)
				.to('websites')
				.to(this.websiteId)
				.to('complexes')

			await importCollection2(data, request, {
				update: true,
			})

			this.refresh = 'hard'
			this.$store.dispatch('app/stopLoading')
			this.$notifications.pushAlert('success', 'Complexes SEO imported!')
		},
	},
}
</script>
